import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Target, Heart, Users, Sparkles } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SEO } from "@/components/SEO";
import { PageHero, SectionTitle } from "@/components/shared";

export default function About() {
  const { settings } = useSettings();
  const [c, setC] = useState({});
  useEffect(() => { api.get("/public/page/about").then((r) => setC(r.data?.content || {})); }, []);

  const blocks = [
    { icon: Target, title: "Our Mission", text: c.mission },
    { icon: Sparkles, title: "Our Vision", text: c.vision },
    { icon: ShieldCheck, title: "Player Safety", text: c.safety },
    { icon: Users, title: "Community Involvement", text: c.community },
  ];

  return (
    <>
      <SEO title={`About BJRC | ${settings?.club_name || "Brackenfell Junior Rugby Club"}`} description="Learn about Brackenfell Junior Rugby Club — our mission, vision, values and player development philosophy." />
      <PageHero title="About BJRC" subtitle="A welcoming, structured junior rugby development club in Brackenfell, Cape Town." image={c.hero_image ? mediaUrl(c.hero_image) : undefined} />

      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <SectionTitle eyebrow="Club Overview" title="Who We Are" />
        <p className="mt-6 text-lg leading-relaxed text-slate-700">{c.overview}</p>
        {c.philosophy && (
          <div className="mt-8 rounded-2xl border-l-4 border-[#E8A317] bg-white p-6 shadow-sm">
            <h3 className="font-heading text-xl font-bold uppercase text-[#0B2A4A]">Player Development Philosophy</h3>
            <p className="mt-2 text-slate-700">{c.philosophy}</p>
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2">
          {blocks.filter((b) => b.text).map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0B2A4A] text-[#E8A317]"><b.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 font-heading text-xl font-bold uppercase text-[#0B2A4A]">{b.title}</h3>
              <p className="mt-2 text-slate-600">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      {Array.isArray(c.values) && c.values.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <SectionTitle center eyebrow="What We Stand For" title="Club Values" />
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            {c.values.map((v) => (
              <div key={v} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-bold uppercase tracking-wide text-[#0B2A4A] shadow-sm ring-1 ring-slate-200">
                <CheckCircle2 className="h-5 w-5 text-[#1E63D6]" />{v}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-[#0B2A4A] p-10 text-center">
          <Heart className="mx-auto h-10 w-10 text-[#E8A317]" />
          <h2 className="mt-4 font-heading text-3xl font-black uppercase text-white">Ready to Get Started?</h2>
          <Link to="/join" className="mt-6 inline-flex rounded-full bg-[#E8A317] px-8 py-3.5 font-extrabold uppercase text-[#0A1626] hover:bg-[#CC8F12]">Join BJRC</Link>
        </div>
      </section>
    </>
  );
}
